import React, { useState, useContext, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaUserFriends, FaEllipsisH, FaChevronLeft, FaCheck } from 'react-icons/fa';
import { FiSliders } from 'react-icons/fi';
import { IoPlay } from 'react-icons/io5';
import { useMediaQuery } from 'react-responsive';
import { AuthContext } from './AuthContext';
import '../styles/LeagueList.css';
import RankingsTable from './RankingsTable';
import leagueService from '../services/leagueService';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	CircularProgress,
	Box,
	Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const LeagueTable = ({
	leagues,
	loading,
	error,
	onLeagueClick,
	onJoinLeague,
	onLeaveLeague,
	openMenuId,
	setOpenMenuId,
	showPrivateLeaguesSection = true,
	emptyMessageKey = 'leagues.noLeagueMember'
}) => {
	const { t } = useTranslation();
	// MOBILE FIRST: mobil je vše do max-width: 768px
	const isMobile = useMediaQuery({ maxWidth: 768 });
	const isDesktop = !isMobile; // zachováme původní podmínky pro širší obrazovky
	const { userName, userId } = useContext(AuthContext);

	const [showRankingsModalId, setShowRankingsModalId] = useState(null);
	const [rankingsData, setRankingsData] = useState([]);
	const [modalLeague, setModalLeague] = useState(null);
	const [rankingsLoading, setRankingsLoading] = useState(false);
	const [rankingsError, setRankingsError] = useState('');
	const [showLeaveModalId, setShowLeaveModalId] = useState(null);
	const [isLeaving, setIsLeaving] = useState(false);

	const [sportFilter, setSportFilter] = useState('ALL');
	const [tournamentFilter, setTournamentFilter] = useState('ALL');
	const [statusFilter, setStatusFilter] = useState('ALL');

	const [visibilityFilter, setVisibilityFilter] = useState('PRIVATE');
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [hoveredCategory, setHoveredCategory] = useState(null);
	const filterMenuRef = useRef(null);
	// Zajistí, že automatické přepnutí na veřejné ligy proběhne jen jednou
	// (nepřepisujeme volbu, kterou si uživatel následně sám nastaví)
	const hasAutoSelectedVisibilityRef = useRef(false);

	// Výchozí zobrazení Přehledu: pokud uživatel nemá žádnou soukromou ligu
	// (nebo jsou všechny jeho soukromé ligy skončené) a zároveň se účastní
	// nějaké veřejné ligy, která ještě běží nebo nezačala, přepneme přepínač
	// automaticky ze Soukromých na Veřejné.
	useEffect(() => {
		if (!showPrivateLeaguesSection) return; // přepínač je jen v režimu "moje ligy"
		if (hasAutoSelectedVisibilityRef.current) return;
		if (loading) return;
		if (!leagues || leagues.length === 0) return;

		hasAutoSelectedVisibilityRef.current = true;

		const hasActivePrivate = leagues.some(
			(l) => l.visibility === 'PRIVATE' && l.status !== 'FINISHED'
		);
		const hasActivePublic = leagues.some(
			(l) => l.visibility === 'PUBLIC' && l.status !== 'FINISHED'
		);

		if (!hasActivePrivate && hasActivePublic) {
			setVisibilityFilter('PUBLIC');
		}
	}, [leagues, loading, showPrivateLeaguesSection]);

	useEffect(() => {
		if (!isFilterOpen) return;
		const handleClickOutside = (event) => {
			if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
				setIsFilterOpen(false);
				setHoveredCategory(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isFilterOpen]);

	const toggleMenu = (leagueId) => {
		setOpenMenuId(openMenuId === leagueId ? null : leagueId);
	};

	const getSportIcon = (sportType) => {
		switch (sportType) {
			case 'FOOTBALL':
				return '⚽';
			case 'HOCKEY':
				return '🏒';
			default:
				return '🏆';
		}
	};

	const getSportLabel = (sportType) => {
		switch (sportType) {
			case 'FOOTBALL':
				return t('football');
			case 'HOCKEY':
				return t('iceHockey');
			default:
				return sportType;
		}
	};

	const handleOpenRankingsModal = async (leagueId) => {
		const leagueToDisplay = leagues.find(l => l.id === leagueId);
		if (!leagueToDisplay) return;

		setShowRankingsModalId(leagueId);
		setModalLeague(leagueToDisplay);
		setRankingsLoading(true);
		setRankingsError('');
		try {
			const response = await leagueService.getLeagueRankings(leagueId);
			setRankingsData(response);
		} catch (err) {
			setRankingsError(t('leagues.failedToFetchRankings'));
			console.error(err);
		} finally {
			setRankingsLoading(false);
		}
	};

	const handleCloseRankingsModal = () => {
		setShowRankingsModalId(null);
		setRankingsData([]);
		setModalLeague(null);
		setOpenMenuId(null);
	};

	const handleConfirmLeave = async () => {
		if (!showLeaveModalId || !onLeaveLeague) return;

		setIsLeaving(true);
		try {
			await onLeaveLeague(showLeaveModalId);
		} catch (err) {
			console.error(err);
		} finally {
			setIsLeaving(false);
			setShowLeaveModalId(null);
			setOpenMenuId(null);
		}
	};

	// Helper function to check if user is league creator
	const isLeagueCreator = (league) => {
		return league.creator?.userId === userId || league.creatorId === userId;
	};

	// Helper function to check if user is member
	const isLeagueMember = (league) => {
		return league.isMember ||
			(league.players && league.players.some(p => p.username === userName || p.userId === userId)) ||
			league.userRanking !== null;
	};

	if (loading) {
		return (
			<div className="leagues-container">
				<div className="leagues-loading">
					<div className="spinner"></div>
					<p>{t('loading')}</p>
				</div>
			</div>
		);
	}

	if (error) {
		return <div className="leagues-container"><div className="leagues-error">{error}</div></div>;
	}

	if (leagues.length === 0 && !error) {
		return (
			<div className="leagues-container">
				<div className="leagues-empty">
					<p>{t(emptyMessageKey)}</p>
				</div>
			</div>
		);
	}

	const statusOrder = { IN_PROGRESS: 0, NOT_STARTED: 1, FINISHED: 2 };
	const sortByStatus = (a, b) => {
		const aOrder = statusOrder[a.status] ?? 99;
		const bOrder = statusOrder[b.status] ?? 99;
		return aOrder - bOrder;
	};

	const sportOptions = Array.from(new Set(leagues.map(l => l.sportType).filter(Boolean)));
	const tournamentOptions = Array.from(
		new Map(
			leagues
				.filter(l => l.tournament?.name)
				.map(l => [
					`${l.tournament.name}__${l.tournament.year ?? ''}`,
					{ key: `${l.tournament.name}__${l.tournament.year ?? ''}`, name: l.tournament.name, year: l.tournament.year }
				])
		).values()
	);

	const matchesFilters = (league) => {
		if (sportFilter !== 'ALL' && league.sportType !== sportFilter) return false;
		if (tournamentFilter !== 'ALL') {
			const key = `${league.tournament?.name ?? ''}__${league.tournament?.year ?? ''}`;
			if (key !== tournamentFilter) return false;
		}
		if (statusFilter !== 'ALL' && league.status !== statusFilter) return false;
		return true;
	};

	const filteredLeagues = leagues.filter(matchesFilters);
	const privateLeagues = filteredLeagues.filter(league => league.visibility === 'PRIVATE').slice().sort(sortByStatus);
	const publicLeagues = filteredLeagues.filter(league => league.visibility === 'PUBLIC').slice().sort(sortByStatus);

	const hasActiveFilters = sportFilter !== 'ALL' || tournamentFilter !== 'ALL' || statusFilter !== 'ALL';
	const activeFilterCount = [sportFilter, tournamentFilter, statusFilter].filter(v => v !== 'ALL').length;

	const statusOptions = [
		{ value: 'IN_PROGRESS', label: t('leagues.IN_PROGRESS') },
		{ value: 'NOT_STARTED', label: t('leagues.NOT_STARTED') },
		{ value: 'FINISHED', label: t('leagues.FINISHED') }
	];

	const filterCategories = [
		{
			key: 'sport',
			label: t('leagues.filterSport'),
			currentValue: sportFilter,
			options: [
				{ value: 'ALL', label: t('leagues.allSports') },
				...sportOptions.map(s => ({ value: s, label: `${getSportIcon(s)} ${getSportLabel(s)}` }))
			],
			onSelect: (value) => setSportFilter(value)
		},
		{
			key: 'tournament',
			label: t('leagues.filterTournament'),
			currentValue: tournamentFilter,
			options: [
				{ value: 'ALL', label: t('leagues.allTournaments') },
				...tournamentOptions.map(opt => ({
					value: opt.key,
					label: t(`tournaments.${opt.name}`, { year: opt.year, defaultValue: opt.name })
				}))
			],
			onSelect: (value) => setTournamentFilter(value)
		},
		{
			key: 'status',
			label: t('leagues.filterStatus'),
			currentValue: statusFilter,
			options: [
				{ value: 'ALL', label: t('leagues.allStatuses') },
				...statusOptions
			],
			onSelect: (value) => setStatusFilter(value)
		}
	];

	const filtersBar = (
		<div className="leagues-toolbar">
			{showPrivateLeaguesSection && (
				<div className="leagues-visibility-toggle" role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={visibilityFilter === 'PRIVATE'}
						className={`visibility-toggle-pill ${visibilityFilter === 'PRIVATE' ? 'is-active' : ''}`}
						onClick={() => setVisibilityFilter('PRIVATE')}
					>
						{t('leagues.privateLeagues')}
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={visibilityFilter === 'PUBLIC'}
						className={`visibility-toggle-pill ${visibilityFilter === 'PUBLIC' ? 'is-active' : ''}`}
						onClick={() => setVisibilityFilter('PUBLIC')}
					>
						{t('leagues.publicLeagues')}
					</button>
				</div>
			)}

			<div className="leagues-filter-dropdown" ref={filterMenuRef}>
				<button
					type="button"
					className={`filter-trigger-btn ${isFilterOpen ? 'is-open' : ''} ${hasActiveFilters ? 'is-active' : ''}`}
					onClick={() => {
						setIsFilterOpen(prev => !prev);
						setHoveredCategory(null);
					}}
					aria-expanded={isFilterOpen}
					aria-haspopup="true"
				>
					<FiSliders className="filter-trigger-icon" />
					<span>{t('leagues.filter', { defaultValue: 'Filtr' })}</span>
					{hasActiveFilters && <span className="filter-trigger-count">{activeFilterCount}</span>}
				</button>

				{isFilterOpen && (
					<div className="filter-menu" onMouseLeave={() => setHoveredCategory(null)}>
						{filterCategories.map(category => (
							<div
								key={category.key}
								className={`filter-menu-item ${hoveredCategory === category.key ? 'is-hovered' : ''}`}
								onMouseEnter={() => setHoveredCategory(category.key)}
								onClick={() => setHoveredCategory(hoveredCategory === category.key ? null : category.key)}
							>
								<FaChevronLeft className="filter-menu-item-chevron" />
								<span className="filter-menu-item-label">
									{category.label}
									{category.currentValue !== 'ALL' && <span className="filter-menu-item-dot" aria-hidden="true" />}
								</span>

								{hoveredCategory === category.key && (
									<div className="filter-submenu" onClick={(e) => e.stopPropagation()}>
										{category.options.map(opt => (
											<div
												key={opt.value}
												className={`filter-submenu-item ${category.currentValue === opt.value ? 'is-selected' : ''}`}
												onClick={() => {
													category.onSelect(opt.value);
													setIsFilterOpen(false);
													setHoveredCategory(null);
												}}
											>
												<span className="filter-submenu-check">
													{category.currentValue === opt.value && <FaCheck />}
												</span>
												<span>{opt.label}</span>
											</div>
										))}
									</div>
								)}
							</div>
						))}

						{hasActiveFilters && (
							<div className="filter-menu-footer">
								<button
									type="button"
									className="filter-menu-clear"
									onClick={() => {
										setSportFilter('ALL');
										setTournamentFilter('ALL');
										setStatusFilter('ALL');
										setIsFilterOpen(false);
										setHoveredCategory(null);
									}}
								>
									{t('leagues.clearFilters', { defaultValue: 'Vyčistit filtry' })}
								</button>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);

	// Pomocná render funkce pro kompaktní řádek (mobil): název + pod tím ranking "1/x" s ikonou
	const MobileLeagueInfo = ({ league, showMemberBadge = false, isMember }) => {
		const total = league.totalPlayers || league.players?.length || 0;
		const rankingText = total === 0 ? '0' : league.userRanking ? `${league.userRanking}/${total}` : `-/${total}`;
		return (
			<div className="mobile-league-info">
				<div className="league-name" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{league.name}</div>
				<div className={`mobile-ranking ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
					<FaUserFriends className="player-icon" />
					<span className="ranking-text">{rankingText}</span>
					{showMemberBadge && isMember && (
						<span className="member-badge">{t('leagues.member')}</span>
					)}
				</div>
			</div>
		);
	};

	const leaveModalContent = showLeaveModalId && (
		ReactDOM.createPortal(
			<div className="invite-modal-overlay" onClick={() => !isLeaving && setShowLeaveModalId(null)}>
				<div className="invite-modal leave-modal" onClick={(e) => e.stopPropagation()}>
					<div className="invite-modal-header">
						<h3 className="invite-modal-title">
							{t('leagues.leaveLeague', { defaultValue: 'Opustit ligu' })}
						</h3>
						<button
							onClick={() => setShowLeaveModalId(null)}
							className="invite-modal-close"
							disabled={isLeaving}
						>
						</button>
					</div>

					<div className="invite-modal-body">
						<p className="modal-description">
							{t('leagues.confirmLeave', { defaultValue: 'Opravdu chcete opustit ligu?' })}
							<br />
							<strong>{leagues.find(l => l.id === showLeaveModalId)?.name}</strong>
						</p>

						<div className="modal-footer-actions">
							<button
								onClick={() => setShowLeaveModalId(null)}
								className="modal-btn-cancel"
								disabled={isLeaving}
							>
								{t('common.cancel', { defaultValue: 'Zrušit' })}
							</button>
							<button
								onClick={handleConfirmLeave}
								className="modal-btn-confirm btn-leave"
								disabled={isLeaving}
							>
								{isLeaving ? t('common.processing', { defaultValue: 'Zpracovávám...' }) : t('leagues.leaveLeague', { defaultValue: 'Opustit' })}
							</button>
						</div>
					</div>
				</div>
			</div>,
			document.body
		)
	);

	return (
		<div className="leagues-container">
			<div className="leagues-content">
				{filtersBar}
				{(() => {
					const showPrivate = showPrivateLeaguesSection ? visibilityFilter === 'PRIVATE' : false;
					const showPublic = showPrivateLeaguesSection ? visibilityFilter === 'PUBLIC' : true;
					const visibleCount =
						(showPrivate ? privateLeagues.length : 0) +
						(showPublic ? publicLeagues.length : 0);
					if (visibleCount === 0) {
						return (
							<div className="leagues-empty">
								<p>{t('leagues.noMatchingFilters')}</p>
							</div>
						);
					}
					return null;
				})()}
				{showPrivateLeaguesSection && visibilityFilter === 'PRIVATE' && privateLeagues.length > 0 && (
					<div className="leagues-section">
						<div className="league-table-container" style={isMobile ? { maxWidth: '100vw', overflowX: 'hidden' } : undefined}>
							<table className="league-table" style={isMobile ? { width: '100%', tableLayout: 'fixed' } : undefined}>
								{isMobile && (
									<colgroup>
										<col style={{ width: 15 }} />
										<col style={{ width: 50 }} />
										<col style={{ width: 15 }} />
									</colgroup>
								)}
								<thead>
									<tr>
										<th className="sport-column" >Sport</th>
										<th>{t('leagues.leagueName')}</th>
										{!isMobile && <th className="tournament-column">{t('leagues.tournament')}</th>}
										{!isMobile && <th className="status-column">{t('leagues.status')}</th>}
										{!isMobile && <th className="players-column">{t('leagues.ranking')}</th>}
										<th className="action-column" ></th>
									</tr>
								</thead>
								<tbody>
									{privateLeagues.map((league) => {
										const isMember = isLeagueMember(league);
										const isCreator = isLeagueCreator(league);

										return (
											<tr key={league.id} className={`league-row ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
												<td className="sport-cell" style={isMobile ? { textAlign: 'center', paddingLeft: 8, paddingRight: 8 } : undefined}>
													<span className="sport-icon">{getSportIcon(league.sportType)}</span>
												</td>
												<td
													className="league-name-cell clickable" style={isMobile ? { whiteSpace: 'normal' } : undefined}
													onClick={() => onLeagueClick(league.id)}
												>
													{isMobile ? (
														<MobileLeagueInfo league={league} />
													) : (
														<>
															<div className="league-name" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{league.name}</div>
															{league.description && (
																<div className="league-description">{league.description}</div>
															)}
														</>
													)}
												</td>
												{!isMobile && (
													<td className="tournament-cell">
														{league.tournament?.name
															? t(`tournaments.${league.tournament.name}`, { year: league.tournament.year })
															: t('noTournament')}
													</td>
												)}
												{!isMobile && (
													<td className="status-cell">
														<div className={
															league.status === 'FINISHED'
																? 'status-finished'
																: league.status === 'IN_PROGRESS'
																	? 'status-in-progress'
																	: 'status-not-started'
														}>
															{league.status ? t(`leagues.${league.status}`) : t('noStatus')}
														</div>
													</td>
												)}
												{!isMobile && (
													<td className="players-cell">
														<div className="players-info">
															{league.userRanking ? (
																<span className={`ranking-info ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
																	<FaUserFriends className="player-icon" />
																	{league.userRanking}/{league.totalPlayers ?? league.players?.length ?? 0}
																</span>
															) : (
																<span className={`no-rank ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
																	<FaUserFriends className="player-icon" />
																	{(league.totalPlayers ?? league.players?.length ?? 0) === 0
																		? '0'
																		: `-/${league.totalPlayers ?? league.players?.length ?? 0}`}
																</span>
															)}
														</div>
													</td>
												)}
												<td
													className="actions-cell"
													style={isMobile ? {
														textAlign: 'right',
														position: 'relative',
														zIndex: openMenuId === league.id ? 10000 : 1
													} : undefined}
												>
													<div className="action-menu-container">
														<button
															type="button"
															aria-label="Více akcí"
															onClick={(e) => { e.stopPropagation(); toggleMenu(league.id); }}
															className="ellipsis-btn"
															style={isMobile ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer' } : undefined}
														>
															<FaEllipsisH style={isMobile ? { fontSize: 16 } : undefined} />
														</button>
														{openMenuId === league.id && (
															<div
																className={`action-menu ${isMobile ? 'mobile-menu' : ''}`}
																style={isMobile ? {
																	// Na mobilu můžeme menu posunout tak, aby se nevešlo ven z obrazovky
																	right: '10px', // trochu od kraje
																	minWidth: '140px',
																	maxWidth: '200px'
																} : {}}
															>
																<div
																	className="action-menu-item"
																	onClick={(e) => {
																		e.stopPropagation();
																		handleOpenRankingsModal(league.id);
																	}}
																>
																	{t('leagues.showRankings')}
																</div>
																{league.status !== 'FINISHED' && isMember && onLeaveLeague && !(isCreator && (league.totalPlayers ?? league.players?.length ?? 0) === 1) && (
																	<div
																		className="action-menu-item danger"
																		onClick={(e) => {
																			e.stopPropagation();
																			setShowLeaveModalId(league.id);
																			setOpenMenuId(null);
																		}}
																	>
																		{t('leagues.leaveLeague')}
																	</div>
																)}
															</div>
														)}
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}

				{(!showPrivateLeaguesSection || visibilityFilter === 'PUBLIC') && publicLeagues.length > 0 && (
					<div className="leagues-section">
						{!showPrivateLeaguesSection && (
							<div className="leagues-section-header">
								<h3 className="leagues-section-title">{t('leagues.publicLeagues')}</h3>
							</div>
						)}
						<div className="league-table-container" style={isMobile ? { maxWidth: '100vw', overflowX: 'hidden' } : undefined}>
							<table className="league-table" style={isMobile ? { width: '100%', tableLayout: 'fixed' } : undefined}>
								{isMobile && (
									<colgroup>
										<col style={{ width: 15 }} />
										<col style={{ width: 50 }} />
										<col style={{ width: 15 }} />
									</colgroup>
								)}
								<thead>
									<tr>
										<th className="sport-column" >Sport</th>
										<th>{t('leagues.leagueName')}</th>
										{!isMobile && <th className="tournament-column">{t('leagues.tournament')}</th>}
										{!isMobile && <th className="status-column">{t('leagues.status')}</th>}
										{!isMobile && <th className="players-column">{t('leagues.ranking')}</th>}
										<th className="action-column" ></th>
									</tr>
								</thead>
								<tbody>
									{publicLeagues.map((league) => {
										const isMember = isLeagueMember(league);
										const isCreator = isLeagueCreator(league);

										return (
											<tr key={league.id} className={`league-row ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
												<td className="sport-cell" style={isMobile ? { textAlign: 'center', paddingLeft: 8, paddingRight: 8 } : undefined}>
													<span className="sport-icon">{getSportIcon(league.sportType)}</span>
												</td>
												<td
													className="league-name-cell clickable" style={isMobile ? { whiteSpace: 'normal' } : undefined}
													onClick={() => onLeagueClick(league.id)}
												>
													{isMobile ? (
														<MobileLeagueInfo league={league} isMember={isMember} showMemberBadge={!league.userRanking && isMember} />
													) : (
														<>
															<div className="league-name" style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{league.name}</div>
															{league.description && (
																<div className="league-description">{league.description}</div>
															)}
														</>
													)}
												</td>
												{!isMobile && (
													<td className="tournament-cell">
														{league.tournament?.name
															? t(`tournaments.${league.tournament.name}`, { year: league.tournament.year })
															: t('noTournament')}
													</td>
												)}
												{!isMobile && (
													<td className="status-cell">
														<div className={
															league.status === 'FINISHED'
																? 'status-finished'
																: league.status === 'IN_PROGRESS'
																	? 'status-in-progress'
																	: 'status-not-started'
														}>
															{league.status ? t(`leagues.${league.status}`) : t('noStatus')}
														</div>
													</td>
												)}
												{!isMobile && (
													<td className="players-cell">
														<div className="players-info">
															{league.userRanking ? (
																<span className={`ranking-info rank-${league.userRanking} ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
																	<FaUserFriends className="player-icon" />
																	{league.userRanking}/{league.totalPlayers ?? league.players?.length ?? 0}
																</span>
															) : (
																<span className={`no-rank ${league.status === 'FINISHED' ? 'finished-league' : ''}`}>
																	<FaUserFriends className="player-icon" />
																	{(league.totalPlayers ?? league.players?.length ?? 0) === 0
																		? '0'
																		: `-/${league.totalPlayers ?? league.players?.length ?? 0}`}
																</span>
															)}
														</div>
													</td>
												)}
												<td
													className="actions-cell"
													style={isMobile ? {
														textAlign: 'right',
														position: 'relative',
														zIndex: openMenuId === league.id ? 10000 : 1
													} : undefined}
												>
													<div className="action-menu-container">
														{onJoinLeague && !isMember && league.status !== 'FINISHED' ? (
															<>
																{isMobile ? (
																	<button
																		className="join-league-button mobile-join-button"
																		onClick={() => onJoinLeague(league.id)}
																	>
																		{t('leagues.joinLeagueMobile')}
																	</button>
																) : (
																	<button
																		className="join-league-button"
																		onClick={() => onJoinLeague(league.id)}
																	>
																		{t('leagues.joinLeague')}
																		<span className="arrow-icon">
																			<IoPlay />
																		</span>
																	</button>
																)}
															</>
														) : (
															<>
																<button
																	type="button"
																	aria-label="Více akcí"
																	onClick={(e) => { e.stopPropagation(); toggleMenu(league.id); }}
																	className="ellipsis-btn"
																	style={isMobile ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', borderRadius: 8, cursor: 'pointer' } : undefined}
																>
																	<FaEllipsisH style={isMobile ? { fontSize: 16 } : undefined} />
																</button>
																{openMenuId === league.id && (
																	<div
																		className={`action-menu ${isMobile ? 'mobile-menu' : ''}`}
																		style={isMobile ? {
																			// Na mobilu můžeme menu posunout tak, aby se nevešlo ven z obrazovky
																			right: '10px', // trochu od kraje
																			minWidth: '140px',
																			maxWidth: '200px'
																		} : {}}
																	>
																		<div
																			className="action-menu-item"
																			onClick={(e) => {
																				e.stopPropagation();
																				handleOpenRankingsModal(league.id);
																			}}
																		>
																			{t('leagues.showRankings')}
																		</div>
																		{isMember && onLeaveLeague && !(isCreator && (league.totalPlayers ?? league.players?.length ?? 0) === 1) && (
																			<div
																				className="action-menu-item danger"
																				onClick={(e) => {
																					e.stopPropagation();
																					setShowLeaveModalId(league.id);
																					setOpenMenuId(null);
																				}}
																			>
																				{t('leagues.leaveLeague')}
																			</div>
																		)}
																	</div>
																)}
															</>
														)}
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>

			<Dialog
				open={Boolean(showRankingsModalId)}
				onClose={handleCloseRankingsModal}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle sx={{ m: 0, p: 2 }}>
					{modalLeague ? t('leagues.ranking') + ' ' + modalLeague.name : ''}
					<IconButton
						aria-label="close"
						onClick={handleCloseRankingsModal}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					{rankingsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
							<CircularProgress />
						</Box>
					) : rankingsError ? (
						<Typography color="error" sx={{ p: 4, textAlign: 'center' }}>
							{rankingsError}
						</Typography>
					) : (
						<RankingsTable
							league={modalLeague}
							rankings={rankingsData}
							selectedDate={null}
						/>
					)}
				</DialogContent>
			</Dialog>
			{leaveModalContent}
		</div>
	);
};

export default LeagueTable;